/**
 * Screens, HUD and menus.
 *
 * All DOM lives here; the game loop never touches an element directly. Screens
 * are translucent so the board keeps playing behind them — the attract loop on
 * the title screen is the real simulation, driven by the same code the player
 * will use.
 */

import { coilName, MODE_ORDER, rulesFor } from '../core/rules'
import type { DeathCause, ModeId, Rules, RunSummary, SimState } from '../core/types'
import { store, type Settings } from '../storage'
import { fetchBoard, hasOnlineBoard, type Board } from './leaderboard'

export type ScreenName = 'none' | 'title' | 'pause' | 'over' | 'board' | 'help' | 'settings'

const DEATH_LINES: Record<DeathCause, string> = {
  wall: 'You struck the lattice.',
  bloom: 'A void bloom opened around you.',
  dark: 'Your light guttered out.',
  self: 'Your fangs had not regrown.',
  none: 'The run ended.',
}

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id)
  if (!el) throw new Error(`missing #${id}`)
  return el as T
}

export interface UIHandlers {
  onPlay: () => void
  onResume: () => void
  onRestart: () => void
  onQuit: () => void
  onModeChange: (mode: ModeId) => void
  onSetting: (patch: Partial<Settings>) => void
  onSubmit: (name: string) => void
  onNavigate: (screen: ScreenName) => void
}

export class UI {
  private app = $('app')
  private screens = new Map<ScreenName, HTMLElement>()
  private current: ScreenName = 'none'
  private boardMode: ModeId = 'ouroboros'
  private boardScope: 'all' | 'today' = 'all'
  private lastScore = -1
  private prevScreen: ScreenName = 'title'

  constructor(
    private handlers: UIHandlers,
    private mode: ModeId,
  ) {
    for (const el of document.querySelectorAll<HTMLElement>('.screen')) {
      this.screens.set(el.dataset.name as ScreenName, el)
    }
    this.wire()
    this.renderModes()
    this.renderSettings()
  }

  // ── screens ────────────────────────────────────────────────────────────

  show(name: ScreenName): void {
    if (name === this.current) return
    if (name !== 'none') this.prevScreen = this.current
    this.current = name
    for (const [key, el] of this.screens) el.classList.toggle('on', key === name)
    this.app.dataset.screen = name
    if (name === 'board') void this.loadBoard()
    if (name === 'over') $('name-input').focus()
  }

  get screen(): ScreenName {
    return this.current
  }

  back(): void {
    this.handlers.onNavigate(this.prevScreen === 'none' ? 'title' : this.prevScreen)
  }

  setMode(mode: ModeId): void {
    this.mode = mode
    this.boardMode = mode
    this.renderModes()
  }

  // ── HUD ────────────────────────────────────────────────────────────────

  syncHud(sim: SimState, rules: Rules): void {
    const score = $('hud-score')
    if (sim.score !== this.lastScore) {
      score.textContent = String(sim.score)
      score.classList.remove('bump')
      void score.offsetWidth // restart the keyframe
      score.classList.add('bump')
      this.lastScore = sim.score
    }
    score.classList.toggle('hot', sim.chain >= 5)

    $('hud-mode').textContent = rules.label
    $('hud-coil').textContent = `Coil ${roman(sim.coil)} · ${coilName(sim.coil)}`
    $('hud-best').textContent = String(Math.max(store.bestFor(rules.id), sim.score))
    $('hud-chain').textContent = sim.chain > 0 ? `×${(1 + sim.chain * 0.25).toFixed(2)} chain` : ''
    $('length-value').textContent = String(sim.snake.length)

    const meter = $('light-meter')
    const fill = $('light-fill')
    meter.classList.remove('idle')
    if (rules.lightEnabled) {
      meter.classList.remove('off')
      const ratio = Math.max(0, sim.light / rules.lightMax)
      fill.style.transform = `scaleX(${ratio})`
      meter.classList.toggle('low', ratio < 0.28)
    } else {
      meter.classList.add('off')
      fill.style.transform = 'scaleX(1)'
    }

    const fangs = $('fangs')
    fangs.classList.toggle('off', !rules.biteEnabled)
    $('hud').classList.toggle('no-fangs', !rules.biteEnabled)
    if (rules.biteEnabled) {
      const ready = sim.biteCooldown === 0 && sim.snake.length >= rules.biteMinLength
      fangs.classList.toggle('ready', ready)
      $('fangs-label').textContent = ready
        ? 'Fangs'
        : sim.snake.length < rules.biteMinLength
          ? 'Too short'
          : `${Math.ceil(sim.biteCooldown / 8)}s`
    }
  }

  /**
   * The rail while nobody is playing. The attract snake's score isn't yours,
   * so showing it would be a lie — it shows the mode and your best instead.
   */
  syncHudIdle(rules: Rules): void {
    const score = $('hud-score')
    score.textContent = '—'
    score.classList.remove('hot')
    this.lastScore = -1
    $('hud-mode').textContent = rules.label
    $('hud-coil').textContent = 'Standby'
    $('hud-best').textContent = String(store.bestFor(rules.id))
    $('hud-chain').textContent = ''
    $('length-value').textContent = '—'
    // Dimmed-but-present in standby, absent only where the rule doesn't exist:
    // hiding it here and revealing it on Play would jog the whole rail.
    const meter = $('light-meter')
    meter.classList.toggle('off', !rules.lightEnabled)
    meter.classList.add('idle')
    meter.classList.remove('low')
    $('light-fill').style.transform = 'scaleX(1)'
    $('fangs').classList.remove('ready')
    $('fangs').classList.toggle('off', !rules.biteEnabled)
    $('hud').classList.toggle('no-fangs', !rules.biteEnabled)
    $('fangs-label').textContent = 'Fangs'
  }

  toast(text: string, tone: 'cyan' | 'gold' | 'magenta' | 'danger' = 'cyan'): void {
    const host = $('toasts')
    const el = document.createElement('div')
    el.className = `toast ${tone === 'cyan' ? '' : tone}`.trim()
    el.textContent = text
    host.append(el)
    // Keep the stack shallow — a bite harvest can fire a dozen of these.
    while (host.children.length > 4) host.firstElementChild?.remove()
    setTimeout(() => el.remove(), 1600)
  }

  // ── game over ──────────────────────────────────────────────────────────

  showGameOver(run: RunSummary, isRecord: boolean, canSubmit: boolean, seconds: number): void {
    $('death-cause').textContent = DEATH_LINES[run.cause]
    $('final-score').textContent = String(run.score)
    const label = $('final-label')
    label.textContent = isRecord ? 'new personal best' : 'score'
    label.classList.toggle('record', isRecord)

    const stats: Array<[string, string | number]> = [
      ['motes', run.motes],
      ['bites', run.bites],
      ['embers', run.embers],
      ['longest', run.longest],
      ['coil', roman(run.coil)],
      ['time', formatDuration(seconds)],
    ]
    $('run-stats').innerHTML = stats
      .map(([k, v]) => `<div class="run-stat"><b>${v}</b><span>${k}</span></div>`)
      .join('')

    const row = $('submit-row')
    row.classList.toggle('hidden', !canSubmit)
    const input = $<HTMLInputElement>('name-input')
    input.value = store.settings.name
    this.setSubmitStatus(
      canSubmit
        ? ''
        : hasOnlineBoard()
          ? 'Score too low to make the board — but it is saved locally.'
          : 'No leaderboard configured. Saved to this device.',
      'neutral',
    )
    this.show('over')
  }

  setSubmitStatus(text: string, tone: 'good' | 'bad' | 'neutral' = 'neutral'): void {
    const el = $('submit-status')
    el.textContent = text
    el.classList.toggle('good', tone === 'good')
    el.classList.toggle('bad', tone === 'bad')
  }

  setSubmitting(on: boolean): void {
    $<HTMLButtonElement>('btn-submit').disabled = on
    $<HTMLInputElement>('name-input').disabled = on
  }

  hideSubmit(): void {
    $('submit-row').classList.add('hidden')
  }

  // ── leaderboard ────────────────────────────────────────────────────────

  private async loadBoard(): Promise<void> {
    const body = $('board-table').querySelector('tbody')
    if (!body) return
    body.innerHTML = `<tr><td class="board-empty" colspan="4">Reading the coil…</td></tr>`
    $('board-note').textContent = ''
    this.renderBoardTabs()
    const board = await fetchBoard(this.boardMode, this.boardScope)
    if (this.current !== 'board') return
    this.renderBoard(board)
  }

  private renderBoardTabs(): void {
    const tabs = $('board-tabs')
    tabs.innerHTML = MODE_ORDER.map(
      (m) =>
        `<button class="tab" role="tab" data-mode="${m}" aria-selected="${m === this.boardMode}">${rulesFor(m).label}</button>`,
    ).join('')
    for (const el of tabs.querySelectorAll<HTMLElement>('.tab')) {
      el.onclick = () => {
        this.boardMode = el.dataset.mode as ModeId
        void this.loadBoard()
      }
    }

    // "Today" only means something on a board that resets daily.
    const scope = $('board-scope')
    if (this.boardMode === 'daily') {
      scope.innerHTML = ''
      this.boardScope = 'today'
      return
    }
    scope.innerHTML = (['all', 'today'] as const)
      .map(
        (s) =>
          `<button class="tab" data-scope="${s}" aria-selected="${s === this.boardScope}">${s === 'all' ? 'All time' : 'Today'}</button>`,
      )
      .join('')
    for (const el of scope.querySelectorAll<HTMLElement>('.tab')) {
      el.onclick = () => {
        this.boardScope = el.dataset.scope as 'all' | 'today'
        void this.loadBoard()
      }
    }
  }

  private renderBoard(board: Board): void {
    const body = $('board-table').querySelector('tbody')
    if (!body) return
    if (!board.entries.length) {
      body.innerHTML = `<tr><td class="board-empty" colspan="4">Nothing here yet. Be the first coil.</td></tr>`
    } else {
      body.innerHTML = board.entries
        .map(
          (e) => `<tr class="${e.you ? 'you' : ''}">
            <td class="r ${e.rank <= 3 ? 'top' : ''}">${e.rank}</td>
            <td class="n">${escapeHtml(e.name)}</td>
            <td class="s">${e.score.toLocaleString()}</td>
            <td class="d">${e.longest} seg · ${e.bites} bites</td>
          </tr>`,
        )
        .join('')
    }
    $('board-note').textContent =
      board.note ??
      (board.source === 'local'
        ? 'Local scores from this device. Configure an endpoint for the online board.'
        : 'Verified by replay — every score here was re-simulated on the server.')
  }

  refreshBoard(): void {
    void this.loadBoard()
  }

  // ── menus ──────────────────────────────────────────────────────────────

  private renderModes(): void {
    const list = $('mode-list')
    list.innerHTML = MODE_ORDER.map((m) => {
      const r = rulesFor(m)
      const best = store.bestFor(m)
      return `<button class="mode" role="radio" data-mode="${m}" aria-checked="${m === this.mode}">
        <span class="mode-dot"></span>
        <span class="mode-name">${r.label}</span>
        <span class="mode-best">${best ? best.toLocaleString() : '—'}</span>
        <span class="mode-tag">${r.tagline}</span>
      </button>`
    }).join('')
    for (const el of list.querySelectorAll<HTMLElement>('.mode')) {
      el.onclick = () => this.handlers.onModeChange(el.dataset.mode as ModeId)
    }
  }

  private renderSettings(): void {
    const s = store.settings
    const toggles: Array<[keyof Settings, string, string]> = [
      ['music', 'Music', 'The drone and the arpeggio that thickens as you descend'],
      ['muted', 'Mute everything', 'Silence, including effects'],
      ['reducedMotion', 'Reduced motion', 'No screen shake, no drifting dust, no head trail'],
      ['showLattice', 'Show lattice', 'The grid your snake lights up as it passes'],
      ['showScanlines', 'Scanlines', 'CRT overlay, for the nostalgic'],
      ['daltonize', 'Colour-blind palette', 'Blue/yellow instead of cyan/gold'],
    ]

    const list = $('settings-list')
    list.innerHTML =
      `<div class="setting">
        <div class="setting-text"><strong>Name</strong><span>Shown on the leaderboard</span></div>
        <input type="text" id="set-name" maxlength="16" value="${escapeHtml(s.name)}" placeholder="anonymous">
      </div>` +
      toggles
        .map(
          ([key, title, desc]) => `<div class="setting">
            <div class="setting-text"><strong>${title}</strong><span>${desc}</span></div>
            <button class="switch" role="switch" data-key="${key}" aria-checked="${Boolean(s[key])}" aria-label="${title}"></button>
          </div>`,
        )
        .join('')

    for (const el of list.querySelectorAll<HTMLElement>('.switch')) {
      el.onclick = () => {
        const key = el.dataset.key as keyof Settings
        const next = el.getAttribute('aria-checked') !== 'true'
        el.setAttribute('aria-checked', String(next))
        this.handlers.onSetting({ [key]: next } as Partial<Settings>)
      }
    }
    const name = $<HTMLInputElement>('set-name')
    name.oninput = () => this.handlers.onSetting({ name: name.value.trim() })
  }

  syncSettings(): void {
    const s = store.settings
    for (const el of document.querySelectorAll<HTMLElement>('.switch')) {
      const key = el.dataset.key as keyof Settings
      el.setAttribute('aria-checked', String(Boolean(s[key])))
      // A lit "music" switch under a lit "mute everything" switch looks broken.
      if (key === 'music') el.closest('.setting')?.classList.toggle('inert', s.muted)
    }
    $('btn-mute').classList.toggle('muted', s.muted)
  }

  // ── wiring ─────────────────────────────────────────────────────────────

  private wire(): void {
    const h = this.handlers
    const click = (id: string, fn: () => void) => {
      $(id).addEventListener('click', fn)
    }

    click('btn-play', h.onPlay)
    click('btn-how', () => h.onNavigate('help'))
    click('btn-board', () => h.onNavigate('board'))
    click('btn-settings', () => h.onNavigate('settings'))

    click('btn-resume', h.onResume)
    click('btn-pause-restart', h.onRestart)
    click('btn-pause-settings', () => h.onNavigate('settings'))
    click('btn-quit', h.onQuit)

    click('btn-again', h.onRestart)
    click('btn-over-board', () => h.onNavigate('board'))
    click('btn-over-title', h.onQuit)
    click('btn-submit', () => h.onSubmit($<HTMLInputElement>('name-input').value.trim()))
    $('name-input').addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') h.onSubmit($<HTMLInputElement>('name-input').value.trim())
    })

    click('btn-board-refresh', () => this.refreshBoard())
    click('btn-board-back', () => this.back())
    click('btn-help-back', () => this.back())
    click('btn-settings-back', () => this.back())
    click('btn-reset-data', () => {
      if (!confirm('Erase local scores, settings and personal bests on this device?')) return
      store.reset()
      location.reload()
    })
  }
}

// ── helpers ──────────────────────────────────────────────────────────────

const ROMAN: Array<[number, string]> = [
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
]

export function roman(n: number): string {
  if (n > 39) return String(n)
  let out = ''
  let v = n
  for (const [value, sym] of ROMAN) {
    while (v >= value) {
      out += sym
      v -= value
    }
  }
  return out || 'I'
}

/**
 * Seconds → m:ss. Wall-clock, measured by the game loop — the simulation is
 * tick-based and has no clock, and tick length changes with every coil, so
 * deriving a duration from the tick count would just be a plausible-looking
 * wrong number.
 */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

export function escapeHtml(str: string): string {
  return str.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`)
}
